import { readFeatureDisplayName } from './ensureNameCustomProperty.js';
import { stringifyPropertyValue } from './stringifyPropertyValue.js';

/**
 * Natural sort and stack sortOrder for imported objects (panel: top = larger label).
 */

/** Case- and locale-friendly compare with numeric segments (IMG_2 < IMG_10). */
export function compareStringsNaturalSort(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export interface VectorLayerStyleForSortLabel {
  styleConfig?: {
    type?: unknown;
    showLabels?: unknown;
    labelProperty?: unknown;
  } | null;
}

/**
 * Resolved display label for vector GeoJSON (aligned with editor layer panel).
 */
function resolvedVectorLabelPropertyExplicitValue(
  layer: VectorLayerStyleForSortLabel | null | undefined,
  props: Record<string, unknown>,
): string | undefined {
  const styleConfig = layer?.styleConfig ?? {};
  const isLabelsEnabled = styleConfig.showLabels === true;
  const labelProperty =
    typeof styleConfig.labelProperty === 'string' ? styleConfig.labelProperty : undefined;

  if (isLabelsEnabled && labelProperty) {
    const labelValue = props[labelProperty];
    if (typeof labelValue === 'string' && labelValue.trim().length > 0) {
      return labelValue.trim();
    }
    if (typeof labelValue === 'number' || typeof labelValue === 'boolean') {
      return String(labelValue);
    }
  }

  return readFeatureDisplayName(props);
}

/** True when the panel/import would synthesize «Точка N» etc. from list index alone. */
export function vectorFeatureUsesIndexOnlyDisplayLabel(
  layer: VectorLayerStyleForSortLabel | null | undefined,
  properties: Record<string, unknown>,
): boolean {
  return resolvedVectorLabelPropertyExplicitValue(layer, properties) === undefined;
}

export function getVectorFeatureDisplaySortLabel(
  layer: VectorLayerStyleForSortLabel | null | undefined,
  properties: Record<string, unknown> | null | undefined,
  geometryType: string | undefined,
  index: number,
): string {
  const props = properties ?? {};

  const explicit = resolvedVectorLabelPropertyExplicitValue(layer, props);
  if (explicit !== undefined) {
    return explicit;
  }

  if (geometryType === 'Point' || geometryType === 'MultiPoint') {
    return `Точка ${index + 1}`;
  }
  if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
    return `Линия ${index + 1}`;
  }
  if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
    return `Полигон ${index + 1}`;
  }
  return `Объект ${index + 1}`;
}

/**
 * Persist default «Точка N» labels from import order onto `properties.name` (and mirrored
 * `labelProperty` when layer shows labels). Without this, the editor derives names from
 * list position and they change after reorder / cross-layer move.
 */
export function persistDefaultImportedVectorFeatureLabels(
  layer: VectorLayerStyleForSortLabel | null | undefined,
  geometryType: string | undefined,
  importOrderIndex: number,
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...properties };
  if (!vectorFeatureUsesIndexOnlyDisplayLabel(layer, out)) {
    return out;
  }

  const label = getVectorFeatureDisplaySortLabel(layer, out, geometryType, importOrderIndex);
  out.name = label;

  const styleConfig = layer?.styleConfig ?? {};
  const isLabelsEnabled = styleConfig.showLabels === true;
  const labelProperty =
    typeof styleConfig.labelProperty === 'string' ? styleConfig.labelProperty : undefined;

  if (isLabelsEnabled && labelProperty) {
    const currentLabel = out[labelProperty];
    const missingForMirror =
      (typeof currentLabel !== 'string' || currentLabel.trim().length === 0) &&
      typeof currentLabel !== 'number' &&
      typeof currentLabel !== 'boolean';

    if (missingForMirror) {
      out[labelProperty] = label;
    }
  }

  return out;
}

export interface Model3DLayerSourceForLabel {
  sourceConfig?: {
    customProperties?: Array<{ name: string; type: string }>;
    modelUrl?: string;
  };
}

/** Panel label for model-3d instance (same as editor). */
export function getModel3DInstanceSortLabel(
  layer: Model3DLayerSourceForLabel | null | undefined,
  instance: {
    label?: string;
    properties?: Record<string, unknown>;
  },
  index: number,
): string {
  const labelField = typeof instance.label === 'string' ? instance.label.trim() : '';
  if (labelField !== '') {
    return labelField;
  }
  const props = (instance.properties ?? {}) as Record<string, unknown>;
  const customProperties = (layer?.sourceConfig?.customProperties ?? []) as Array<{
    name: string;
    type: string;
  }>;
  const titleProp = customProperties.find((p) => p.type === 'title');
  if (titleProp) {
    const tv = props[titleProp.name];
    if (tv != null && stringifyPropertyValue(tv).trim() !== '') {
      return stringifyPropertyValue(tv).trim();
    }
  }
  const name = readFeatureDisplayName(props);
  if (name !== undefined) {
    return name;
  }
  return `Объект ${index + 1}`;
}

/** Panel label for georeferenced image instance (same as editor). */
export function getImageOverlayInstanceSortLabel(
  instance: { label?: string },
  index: number,
): string {
  const raw = instance.label?.trim();
  if (raw) {
    return raw;
  }
  return `Изображение ${index + 1}`;
}

/**
 * For each entry index, sortOrder so that ascending DB order (sort_order ASC) matches panel:
 * top row = lexicographically largest label, bottom = smallest.
 * @param labels — label per feature/instance, same length as batch
 */
export function mapLabelsToStackSortOrders(labels: readonly string[]): number[] {
  const n = labels.length;
  if (n === 0) {
    return [];
  }
  const indices = labels.map((_, i) => i);
  indices.sort((ia, ib) =>
    compareStringsNaturalSort(labels[ia] ?? '', labels[ib] ?? ''),
  );
  const byOriginalIndex = Array.from<number>({ length: n });
  for (let rank = 0; rank < n; rank++) {
    const originalIndex = indices[rank]!;
    byOriginalIndex[originalIndex] = n - 1 - rank;
  }
  return byOriginalIndex;
}
